import { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { safeGetAccessToken } from "../utils/auth";
import { toast, ToastContainer } from "react-toastify";
import { Sparkles, AlertTriangle, ArrowRight, MapPin } from "lucide-react";
import { API_BASE, TOASTPROPS } from "../shared/Constants";
import { parseDate } from "../shared/Utils";
import { reportHandledError, reportApiError } from "../utils/reportError";
import { Card } from "./ui/card";
import { Skeleton } from "./ui/skeleton";
import { Badge } from "./ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

interface Model {
  max_tokens: number;
  update_date: string;
  knowledge_cutoff?: string;
  alias?: string;
  deprecation_date?: string | null;
  replacement_model?: string | null;
  [key: string]: any;
}

interface ModelList {
  [modelName: string]: Model;
}

type Status = "atual" | "rapido" | "deprecating" | "legacy";

function classify(name: string, m: Model): Status {
  if (m.deprecation_date) return "deprecating";
  const lower = name.toLowerCase();
  const versionMatch = lower.match(/-(\d+(?:\.\d+)?)$|-(\d+(?:\.\d+)?)-/);
  const version = versionMatch ? parseFloat(versionMatch[1] || versionMatch[2]) : 0;
  if (version >= 4) return "atual";
  return "legacy";
}

// Modelos com inferência e processamento realizados 100% em território nacional
// (soberania/residência de dados). Identificados pelo sufixo "br-sp" no nome ou alias.
function isNationalTerritory(name: string, m: Model): boolean {
  return `${name} ${m.alias ?? ""}`.toLowerCase().includes("br-sp");
}

function StatusBadge({ status, deprecationDate }: { status: Status; deprecationDate?: string | null }) {
  if (status === "atual") {
    return <Badge variant="success">Atual</Badge>;
  }
  if (status === "rapido") {
    return <Badge variant="accent">Rápido</Badge>;
  }
  if (status === "deprecating" && deprecationDate) {
    return (
      <Badge variant="warning" className="whitespace-nowrap">
        <AlertTriangle className="h-3 w-3" />
        Descontinuação em {parseDate(deprecationDate)}
      </Badge>
    );
  }
  return <Badge variant="secondary">Legacy</Badge>;
}

export default function Models() {
  const [loading, setLoading] = useState<boolean>(true);
  const [models, setModels] = useState<ModelList>({});
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  useEffect(() => {
    const get = async () => {
      try {
        setLoading(true);
        const token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
        if (!token) return;
        const response = await fetch(`${API_BASE}/api/chat/models`, {
          method: "GET",
          headers: { Authorization: `Auth ${token}` },
        });
        if (!response.ok) {
          reportApiError(response, { feature: "models.list", method: "GET" });
          throw new Error("Failed");
        }
        const data = await response.json();
        setModels(data["models"] || {});
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "models.list" });
        toast.error("Erro ao recuperar modelos disponíveis.", TOASTPROPS);
      } finally {
        setLoading(false);
      }
    };
    get();
  }, [getAccessTokenSilently]);

  const rows = useMemo(() => {
    const visible = Object.keys(models).filter(
      (n) => !n.toLowerCase().includes("competidor")
    );
    // Order: atual → rapido → deprecating → legacy, then by name desc within group
    const ordered = visible.sort((a, b) => {
      const order: Record<Status, number> = { atual: 0, rapido: 1, deprecating: 2, legacy: 3 };
      const oa = order[classify(a, models[a])];
      const ob = order[classify(b, models[b])];
      if (oa !== ob) return oa - ob;
      return b.localeCompare(a);
    });
    return ordered;
  }, [models]);

  return (
    <TooltipProvider>
    <div className="space-y-4">
      <Card>
        <div className="flex items-center gap-3 border-b border-border px-5 py-4">
          <Sparkles className="h-4 w-4 text-muted-foreground" />
          <div>
            <p className="text-[14px] font-semibold text-foreground">Catálogo de modelos</p>
            <p className="text-[12px] text-muted-foreground">
              {loading ? "Carregando..." : `${rows.length} ${rows.length === 1 ? "modelo disponível" : "modelos disponíveis"}`}
            </p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                <th className="px-5 py-3">Modelo</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Substituto</th>
                <th className="px-5 py-3 text-right">Contexto máximo</th>
                <th className="px-5 py-3">Lançamento</th>
                <th className="px-5 py-3">Outros nomes</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-border/60">
                      <td className="px-5 py-4"><Skeleton className="h-4 w-32" /></td>
                      <td className="px-5 py-4"><Skeleton className="h-4 w-16" /></td>
                      <td className="px-5 py-4"><Skeleton className="h-4 w-20" /></td>
                      <td className="px-5 py-4"><Skeleton className="ml-auto h-4 w-24" /></td>
                      <td className="px-5 py-4"><Skeleton className="h-4 w-24" /></td>
                      <td className="px-5 py-4"><Skeleton className="h-4 w-24" /></td>
                      <td className="px-5 py-4"><Skeleton className="h-4 w-40" /></td>
                    </tr>
                  ))
                : rows.map((name) => {
                    const m = models[name];
                    const status = classify(name, m);
                    const national = isNationalTerritory(name, m);
                    const aliases = m.alias ? m.alias.split(",").map((a) => a.trim()) : [];
                    return (
                      <tr key={name} className="border-b border-border/60 transition-colors hover:bg-muted/30">
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="font-mono text-[13px] font-semibold text-foreground">{name}</p>
                            {national && (
                              <Tooltip delayDuration={300}>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex cursor-default">
                                    <Badge variant="outline" className="gap-1">
                                      <MapPin className="h-3 w-3" />
                                      Território nacional
                                    </Badge>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs text-left">
                                  Inferência e processamento realizados 100% em território nacional, para casos de uso que exigem soberania e residência de dados no Brasil.
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <StatusBadge status={status} deprecationDate={m.deprecation_date} />
                        </td>
                        <td className="px-5 py-4">
                          {m.replacement_model ? (
                            <span className="inline-flex items-center gap-1 font-mono text-[12px] text-foreground">
                              <ArrowRight className="h-3 w-3 text-muted-foreground" />
                              {m.replacement_model}
                            </span>
                          ) : (
                            <span className="text-[13px] text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-5 py-4 text-right font-mono text-[13px] text-foreground">
                          {m.max_tokens.toLocaleString()} tokens
                        </td>
                        <td className="px-5 py-4 text-[13px] text-muted-foreground">
                          {parseDate(m.update_date)}
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap gap-1">
                            {aliases.length === 0 ? (
                              <span className="text-[13px] text-muted-foreground">—</span>
                            ) : (
                              aliases.map((a) => (
                                <code
                                  key={a}
                                  className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground"
                                >
                                  {a}
                                </code>
                              ))
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-3">
          <div className="mb-1 flex items-center gap-2">
            <Badge variant="success">Atual</Badge>
          </div>
          <p className="text-[12px] text-muted-foreground">
            Versão mais recente. Recomendamos usar para novos projetos.
          </p>
        </div>
        <div className="rounded-lg border border-border bg-card p-3">
          <div className="mb-1 flex items-center gap-2">
            <Badge variant="warning">
              <AlertTriangle className="h-3 w-3" />
              Descontinuação
            </Badge>
          </div>
          <p className="text-[12px] text-muted-foreground">
            Modelo segue disponível até a data anunciada. Planeje a migração para o modelo substituto.
          </p>
        </div>
        <div className="rounded-lg border border-border bg-card p-3">
          <div className="mb-1 flex items-center gap-2">
            <Badge variant="secondary">Legacy</Badge>
          </div>
          <p className="text-[12px] text-muted-foreground">
            Versões anteriores ainda disponíveis para compatibilidade.
          </p>
        </div>
      </div>

      <ToastContainer />
    </div>
    </TooltipProvider>
  );
}